import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger
} from "../components/ui/accordion";
import { Link } from 'react-router-dom';
import { useAudio } from '../contexts/AudioContext';
import { AudioPlayer } from '../components/AudioPlayer';
import {
    Search, BookOpen, FileText, Loader2, Headphones,
    ExternalLink, PlayCircle, Music, Calendar, Mic2,
    GraduationCap, ChevronRight, Folder, PauseCircle,
    ArrowLeft, Sparkles, Library, Radio, BookMarked, LogIn, UserPlus
} from 'lucide-react';
import { loadKhassaides, loadMediatheque, EMPTY_MEDIA } from '../lib/loadData';

export default function PublicMediatheque() {
    const [activeTab, setActiveTab] = useState('pdf');
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedPdf, setSelectedPdf] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [pdfDisplayCount, setPdfDisplayCount] = useState(50);
    const [isVisible, setIsVisible] = useState(false);

    // Données volumineuses chargées à la demande (hors bundle principal)
    const [khassaidesData, setKhassaidesData] = useState([]);
    const [mediathequeData, setMediathequeData] = useState(EMPTY_MEDIA);
    useEffect(() => {
        loadKhassaides().then(setKhassaidesData).catch(console.error);
        loadMediatheque().then(setMediathequeData).catch(console.error);
    }, []);

    // Audio Context
    const {
        currentTrack,
        isPlaying,
        playPlaylist,
        nextTrack,
        previousTrack,
        closePlayer,
        hasNext,
        hasPrevious
    } = useAudio();

    // Animation on mount
    useEffect(() => {
        setIsVisible(true);
    }, []);

    const pdfCategories = ["Tous", "Quran", "Français", "Anglais", "Wolof"];
    const [selectedPdfCategory, setSelectedPdfCategory] = useState('Tous');

    // Filtering logic
    const filteredPdfs = khassaidesData.filter(item => {
        const matchesSearch = item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (item.translation && item.translation.toLowerCase().includes(searchTerm.toLowerCase()));
        const matchesCategory = selectedPdfCategory === 'Tous' || item.tags.some(tag => tag.toLowerCase() === selectedPdfCategory.toLowerCase());
        return matchesSearch && matchesCategory;
    });

    const handleOpenPdf = (pdf) => {
        setSelectedPdf(pdf);
        setIsLoading(true);
    };

    const handleOpenExternal = (url) => window.open(url, '_blank', 'noopener,noreferrer');

    const playSectionTracks = (tracks, startIndex, meta) => {
        const formattedTracks = tracks.map((t, i) => ({
            id: `${meta.prefix}-${i}`,
            title: t.title,
            author: meta.author || t.author || "Inconnu",
            year: meta.year || t.year || "",
            url: t.url
        }));
        playPlaylist(formattedTracks, startIndex);
    };

    const tabIcons = {
        pdf: BookOpen,
        audio: Headphones,
        coran: BookMarked,
        wolofal: Mic2,
        xamxam: GraduationCap,
        kourel: Music,
        radiass: Radio
    };

    const tabColors = {
        pdf: 'from-blue-500 to-cyan-500',
        audio: 'from-violet-500 to-purple-500',
        coran: 'from-emerald-500 to-green-500',
        wolofal: 'from-amber-500 to-yellow-500',
        xamxam: 'from-indigo-500 to-blue-500',
        kourel: 'from-pink-500 to-rose-500',
        radiass: 'from-orange-500 to-red-500'
    };

    const TrackButton = ({ track, tracks, index, meta, isPlaying }) => (
        <Button
            variant="ghost"
            className={`justify-start h-auto py-3 px-3 text-left whitespace-normal group ${isPlaying ? 'bg-primary/10 text-primary' : 'hover:bg-muted/80'}`}
            onClick={() => playSectionTracks(tracks, index, meta)}
        >
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center mr-3 shrink-0 ${isPlaying ? 'bg-primary text-white' : 'bg-muted group-hover:bg-primary/20'}`}>
                {isPlaying ? <PauseCircle className="h-4 w-4" /> : <PlayCircle className="h-4 w-4" />}
            </div>
            <span className="text-sm line-clamp-2">{track.title}</span>
        </Button>
    );

    return (
        <div className="min-h-screen bg-gradient-to-b from-background via-background to-muted/30 pb-12 overflow-x-hidden" data-testid="public-mediatheque-page">
            {/* Decorative Background Elements */}
            <div className="fixed inset-0 z-0 pointer-events-none overflow-hidden">
                <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-primary/5 rounded-full blur-3xl transform translate-x-1/2 -translate-y-1/2" />
                <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-secondary/10 rounded-full blur-3xl transform -translate-x-1/2 translate-y-1/2" />
            </div>

            {/* Navigation & Header */}
            <div className={`relative z-10 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'}`}>
                <div className="container mx-auto px-4 md:px-8 py-4 safe-top">
                    <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mb-8">
                        <Link to="/" className="self-start sm:self-auto">
                            <Button variant="ghost" size="sm" className="gap-2 rounded-2xl hover:bg-white/80 transition-all duration-300 font-bold text-slate-600">
                                <ArrowLeft className="h-4 w-4" />
                                <span className="hidden sm:inline">Retour à l'accueil</span>
                                <span className="inline sm:hidden text-xs">Accueil</span>
                            </Button>
                        </Link>
                        <div className="flex items-center gap-2 md:gap-4 bg-white/40 backdrop-blur-md p-1 rounded-[2rem] border border-white/50 shadow-sm sm:w-auto">
                            <Link to="/login">
                                <Button variant="ghost" size="sm" className="gap-2 rounded-[1.5rem] px-3 md:px-5 font-bold hover:bg-white/60 transition-all duration-300">
                                    <LogIn className="h-4 w-4 text-primary" />
                                    <span className="hidden sm:inline">Connexion</span>
                                </Button>
                            </Link>
                            <Link to="/register">
                                <Button size="sm" className="gap-2 bg-slate-900 text-white hover:bg-slate-800 hover:shadow-xl transition-all duration-500 rounded-[1.5rem] px-3 md:px-6 font-bold">
                                    <UserPlus className="h-4 w-4 text-emerald-400" />
                                    <span className="hidden sm:inline">S'inscrire</span>
                                </Button>
                            </Link>
                        </div>
                    </div>

                    {/* Hero Section */}
                    <div className="relative overflow-hidden rounded-[2rem] md:rounded-[2.5rem] bg-gradient-to-br from-violet-600 via-purple-600 to-fuchsia-700 p-6 md:p-10 text-white shadow-2xl mb-8">
                        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PHBhdGggZD0iTTM2IDM0djZoNnYtNmgtNnptMCAwdi02aC02djZoNnptLTYgNmgtNnY2aDZ2LTZ6bTYgMHY2aDZ2LTZoLTZ6Ii8+PC9nPjwvZz48L3N2Zz4=')] opacity-30" />
                        <div className="relative z-10 flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6 md:gap-8">
                            <div className="space-y-3 md:space-y-4">
                                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 backdrop-blur-md border border-white/10 text-[9px] md:text-[10px] font-bold tracking-widest uppercase">
                                    <Sparkles className="h-3 w-3 text-yellow-300" /> Archives de la Daara
                                </div>
                                <h1 className="text-2xl md:text-4xl lg:text-5xl font-black tracking-tight leading-tight">Médiathèque</h1>
                                <p className="text-purple-100/80 max-w-md text-sm md:text-base leading-relaxed font-medium">
                                    Explorez notre collection de Khassaides, récitations coraniques et enseignements.
                                </p>
                            </div>
                            <div className="relative w-full lg:w-96 group">
                                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-purple-300 group-focus-within:text-white transition-colors" />
                                <Input
                                    placeholder="Rechercher..."
                                    className="pl-12 h-12 md:h-14 bg-white/10 border-white/20 text-white placeholder:text-purple-200 rounded-2xl backdrop-blur-md focus:ring-white/30 focus:border-white/30 text-lg"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="relative z-10 container mx-auto px-4 md:px-8">
                <Tabs defaultValue="pdf" value={activeTab} onValueChange={setActiveTab} className="space-y-8">
                    {/* Category Navigation */}
                    <div className="overflow-x-auto pb-4 -mx-4 px-4 no-scrollbar">
                        <TabsList className="inline-flex h-auto p-2 bg-muted/50 rounded-[2rem] gap-2 border border-slate-200/50 backdrop-blur-xl">
                            {mediathequeData.sections.map((section) => {
                                const Icon = tabIcons[section.id] || Folder;
                                const isActive = activeTab === section.id;
                                return (
                                    <TabsTrigger
                                        key={section.id}
                                        value={section.id}
                                        className={`px-3 md:px-6 py-2.5 md:py-3 rounded-[1.2rem] md:rounded-[1.5rem] transition-all duration-500 gap-0 md:gap-3 whitespace-nowrap ${isActive ? 'bg-white shadow-xl text-primary font-bold' : 'hover:bg-white/50'
                                            }`}
                                    >
                                        <div className={`w-7 h-7 md:w-8 md:h-8 rounded-lg md:rounded-xl flex items-center justify-center transition-transform duration-500 ${isActive ? `bg-gradient-to-br ${tabColors[section.id]} text-white scale-110 shadow-lg` : 'bg-muted'
                                            }`}>
                                            <Icon className="h-3.5 w-3.5 md:h-4 md:w-4" />
                                        </div>
                                        <span className="hidden md:inline font-bold tracking-tight text-sm">{section.label}</span>
                                    </TabsTrigger>
                                );
                            })}
                        </TabsList>
                    </div>

                    <div className="min-h-[60vh] pb-24">
                        {/* PDF Content */}
                        <TabsContent value="pdf" className="m-0 space-y-8 animate-in fade-in-50 slide-in-from-bottom-4 duration-500">
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                                <div className="flex flex-wrap items-center gap-2 md:gap-3">
                                    {pdfCategories.map(cat => (
                                        <Badge
                                            key={cat}
                                            variant={selectedPdfCategory === cat ? "default" : "outline"}
                                            className={`cursor-pointer px-4 md:px-6 py-2 md:py-2.5 text-xs md:text-sm rounded-full transition-all duration-300 font-bold ${selectedPdfCategory === cat
                                                ? 'bg-gradient-to-r from-blue-500 to-cyan-500 border-0 shadow-lg shadow-blue-500/20'
                                                : 'hover:bg-white hover:border-blue-300 border-slate-200'
                                                }`}
                                            onClick={() => { setSelectedPdfCategory(cat); setPdfDisplayCount(50); }}
                                        >
                                            {cat}
                                        </Badge>
                                    ))}
                                </div>
                                <span className="text-[10px] md:text-sm font-black text-slate-400 uppercase tracking-[0.2em] sm:ml-auto">{filteredPdfs.length} KHASSAIDES</span>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                                {filteredPdfs.slice(0, pdfDisplayCount).map((item, index) => (
                                    <Card
                                        key={index}
                                        className="group hover:shadow-2xl hover:-translate-y-1.5 transition-all duration-500 cursor-pointer border-slate-100 dark:border-slate-800 overflow-hidden rounded-[2rem] bg-white/50 backdrop-blur-sm"
                                        onClick={() => handleOpenPdf(item)}
                                    >
                                        <div className="h-2 bg-gradient-to-r from-blue-500 to-cyan-500 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                                        <CardContent className="p-4 md:p-6 flex flex-row items-center gap-3 md:gap-4">
                                            <div className="h-12 w-12 md:h-14 md:w-14 shrink-0 rounded-xl md:rounded-2xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center text-white shadow-xl shadow-blue-500/20 group-hover:rotate-6 transition-transform duration-500">
                                                <FileText className="h-6 w-6 md:h-7 md:w-7" />
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <h3 className="font-bold truncate text-sm md:text-base text-slate-900 dark:text-slate-100 group-hover:text-blue-600 transition-colors uppercase">{item.title}</h3>
                                                <p className="text-[9px] md:text-[10px] font-black uppercase tracking-widest text-slate-400 mt-1 truncate">{item.tags?.join(' • ')}</p>
                                            </div>
                                            <ChevronRight className="h-4 w-4 md:h-5 md:w-5 text-slate-300 group-hover:text-blue-500 group-hover:translate-x-1 transition-all duration-500" />
                                        </CardContent>
                                    </Card>
                                ))}
                            </div>

                            {pdfDisplayCount < filteredPdfs.length && (
                                <div className="flex justify-center pt-8">
                                    <Button
                                        variant="outline"
                                        onClick={() => setPdfDisplayCount(prev => prev + 50)}
                                        className="gap-3 rounded-2xl h-14 px-8 border-slate-200 font-bold hover:bg-white hover:shadow-xl transition-all"
                                    >
                                        <Sparkles className="h-5 w-5 text-amber-500" />
                                        Charger plus de ressources
                                    </Button>
                                </div>
                            )}
                        </TabsContent>

                        {/* Audio Content */}
                        <TabsContent value="audio" className="m-0 space-y-12 animate-in fade-in-50 slide-in-from-bottom-4 duration-500">
                            {mediathequeData.audio_archives.map((archive, idx) => (
                                <div key={idx} className="space-y-6">
                                    <div className="flex items-center gap-4">
                                        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center text-white shadow-xl">
                                            <Folder className="h-6 w-6" />
                                        </div>
                                        <h2 className="text-2xl font-black tracking-tight">{archive.category}</h2>
                                    </div>

                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                        {archive.groups?.map((group) => (
                                            <Card key={group.id} className="overflow-hidden border-slate-100 rounded-[2.5rem] bg-white/50 backdrop-blur-sm hover:shadow-2xl transition-all duration-500">
                                                <CardHeader className="bg-gradient-to-r from-violet-50 to-transparent pb-6 p-8">
                                                    <CardTitle className="text-xl font-bold flex items-center gap-3">
                                                        <Music className="h-6 w-6 text-violet-500" />
                                                        {group.name}
                                                    </CardTitle>
                                                    <CardDescription className="font-medium">Archives historiques et événements</CardDescription>
                                                </CardHeader>
                                                <CardContent className="p-2 px-6">
                                                    <Accordion type="single" collapsible className="w-full">
                                                        {group.years.map((yearItem, i) => (
                                                            <AccordionItem key={i} value={`year-${i}`} className="border-b-0">
                                                                <AccordionTrigger className="hover:no-underline py-4 px-4 rounded-2xl hover:bg-violet-50/50 transition-colors">
                                                                    <span className="flex items-center gap-3 text-sm font-bold">
                                                                        <Calendar className="h-4 w-4 text-violet-400" />
                                                                        {yearItem.year} — {yearItem.event}
                                                                    </span>
                                                                </AccordionTrigger>
                                                                <AccordionContent className="pb-4 pt-2">
                                                                    {yearItem.tracks ? (
                                                                        <div className="flex flex-col space-y-1.5 px-2">
                                                                            {yearItem.tracks.map((track, trackIdx) => (
                                                                                <TrackButton
                                                                                    key={trackIdx}
                                                                                    track={track}
                                                                                    tracks={yearItem.tracks}
                                                                                    index={trackIdx}
                                                                                    meta={{ prefix: `${group.id}-${yearItem.year}`, author: group.name, year: yearItem.year }}
                                                                                    isPlaying={currentTrack?.url === track.url}
                                                                                />
                                                                            ))}
                                                                        </div>
                                                                    ) : (
                                                                        <div className="flex items-center justify-between p-4 text-sm font-medium text-slate-500 bg-slate-50 rounded-2xl mx-2">
                                                                            <span>Archive externe disponible</span>
                                                                            <Button variant="link" size="sm" onClick={() => handleOpenExternal(yearItem.url)} className="h-auto p-0 font-bold text-violet-600">
                                                                                Ouvrir <ExternalLink className="ml-1.5 h-3.5 w-3.5" />
                                                                            </Button>
                                                                        </div>
                                                                    )}
                                                                </AccordionContent>
                                                            </AccordionItem>
                                                        ))}
                                                    </Accordion>
                                                </CardContent>
                                            </Card>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </TabsContent>

                        {/* Coran Content */}
                        <TabsContent value="coran" className="m-0 space-y-8 animate-in fade-in-50 slide-in-from-bottom-4 duration-500">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                {mediathequeData.coran_archives?.map((item, index) => (
                                    <Card key={index} className="overflow-hidden border-slate-100 rounded-[2.5rem] bg-white/50 backdrop-blur-sm hover:shadow-2xl transition-all duration-500">
                                        <CardHeader className="bg-gradient-to-r from-emerald-50 to-transparent p-8 pb-6">
                                            <div className="flex items-center gap-4">
                                                <div className="h-16 w-16 rounded-[2rem] bg-gradient-to-br from-emerald-500 to-green-600 flex items-center justify-center shadow-xl shadow-emerald-500/20">
                                                    <BookMarked className="h-8 w-8 text-white" />
                                                </div>
                                                <div>
                                                    <CardTitle className="text-2xl font-black">{item.reciter}</CardTitle>
                                                    <CardDescription className="font-bold text-emerald-600 uppercase tracking-widest text-[10px]">{item.tracks?.length || 0} Sourates disponibles</CardDescription>
                                                </div>
                                            </div>
                                        </CardHeader>
                                        <CardContent className="p-0">
                                            <ScrollArea className="h-[450px]">
                                                <div className="flex flex-col p-4 px-8 space-y-1.5">
                                                    {item.tracks.map((track, trackIdx) => (
                                                        <TrackButton
                                                            key={trackIdx}
                                                            track={track}
                                                            tracks={item.tracks}
                                                            index={trackIdx}
                                                            meta={{ prefix: `coran-${index}`, author: item.reciter, year: "Coran" }}
                                                            isPlaying={currentTrack?.url === track.url}
                                                        />
                                                    ))}
                                                </div>
                                            </ScrollArea>
                                        </CardContent>
                                    </Card>
                                ))}
                            </div>
                        </TabsContent>

                        {/* Repeat for other sections (Wolofal, etc.) if needed */}
                        {['wolofal', 'xamxam', 'kourel', 'radiass'].map(sectionId => (
                            <TabsContent key={sectionId} value={sectionId} className="m-0 space-y-8 animate-in fade-in-50 slide-in-from-bottom-4 duration-500">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                    {mediathequeData[`${sectionId}_archives`]?.map((item, index) => {
                                        const Icon = tabIcons[sectionId] || Folder;
                                        return (
                                            <Card key={index} className="overflow-hidden border-slate-100 rounded-[2.5rem] bg-white/50 backdrop-blur-sm hover:shadow-2xl transition-all duration-500">
                                                <CardHeader className="bg-gradient-to-r from-muted/50 to-transparent p-8 pb-6">
                                                    <div className="flex items-center gap-4">
                                                        <div className={`h-16 w-16 rounded-[2rem] bg-gradient-to-br ${tabColors[sectionId]} flex items-center justify-center shadow-xl`}>
                                                            <Icon className="h-8 w-8 text-white" />
                                                        </div>
                                                        <div className="min-w-0 flex-1">
                                                            <CardTitle className="text-xl font-black truncate">{item.title || item.author}</CardTitle>
                                                            <CardDescription className="font-bold truncate opacity-70 uppercase tracking-widest text-[10px]">{item.tracks?.length || 0} Pistes</CardDescription>
                                                        </div>
                                                    </div>
                                                </CardHeader>
                                                <CardContent className="p-0">
                                                    <ScrollArea className="h-[400px]">
                                                        <div className="flex flex-col p-4 px-8 space-y-1.5">
                                                            {item.tracks?.map((track, trackIdx) => (
                                                                <TrackButton
                                                                    key={trackIdx}
                                                                    track={track}
                                                                    tracks={item.tracks}
                                                                    index={trackIdx}
                                                                    meta={{ prefix: `${sectionId}-${index}`, author: item.author || item.title, year: item.title || sectionId }}
                                                                    isPlaying={currentTrack?.url === track.url}
                                                                />
                                                            ))}
                                                        </div>
                                                    </ScrollArea>
                                                </CardContent>
                                            </Card>
                                        );
                                    })}
                                </div>
                            </TabsContent>
                        ))}
                    </div>
                </Tabs>
            </div>

            {/* PDF Reader Dialog */}
            <Dialog open={!!selectedPdf} onOpenChange={(open) => !open && setSelectedPdf(null)}>
                <DialogContent className="max-w-5xl h-[90vh] flex flex-col p-0 gap-0 overflow-hidden rounded-[2.5rem] border-0">
                    <DialogHeader className="p-4 md:p-6 border-b flex flex-col md:flex-row items-start md:items-center justify-between gap-4 bg-white/80 backdrop-blur-lg">
                        <DialogTitle className="flex items-center gap-3 md:gap-4 w-full md:w-auto">
                            <div className="h-10 w-10 md:h-14 md:w-14 shrink-0 rounded-xl md:rounded-2xl bg-blue-500/10 flex items-center justify-center">
                                <FileText className="h-5 w-5 md:h-7 md:w-7 text-blue-600" />
                            </div>
                            <div className="min-w-0 flex-1">
                                <span className="block font-black text-lg md:text-xl truncate">{selectedPdf?.title}</span>
                                <span className="text-[8px] md:text-[10px] font-black uppercase tracking-widest text-slate-400">Lecteur PDF Haute Définition</span>
                            </div>
                        </DialogTitle>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => window.open(selectedPdf?.pdfUrl, '_blank')}
                            className="w-full md:w-auto gap-2 md:gap-3 rounded-xl md:rounded-2xl border-slate-200 font-bold hover:shadow-lg transition-all"
                        >
                            <ExternalLink className="h-4 w-4 md:h-5 md:w-5" />
                            Ouvrir dans un onglet
                        </Button>
                    </DialogHeader>
                    <div className="flex-1 bg-slate-900 relative">
                        {isLoading && (
                            <div className="absolute inset-0 flex items-center justify-center bg-slate-900/80 backdrop-blur-sm z-10">
                                <Loader2 className="h-10 w-10 md:h-12 md:w-12 animate-spin text-white" />
                            </div>
                        )}
                        {selectedPdf && (
                            <iframe
                                src={`${selectedPdf.pdfUrl}#toolbar=0`}
                                className="w-full h-full border-none"
                                onLoad={() => setIsLoading(false)}
                                title={selectedPdf.title}
                            />
                        )}
                    </div>
                </DialogContent>
            </Dialog>

            {/* Audio Player Integration */}
            {currentTrack && (
                <AudioPlayer
                    track={currentTrack}
                    onClose={closePlayer}
                    onNext={hasNext ? nextTrack : null}
                    onPrevious={hasPrevious ? previousTrack : null}
                />
            )}
        </div>
    );
}
